import { useState } from 'react'
import EmptyState from '@/components/shared/EmptyState'
import { supabase } from '@/lib/supabase'
import { showToast } from '@/components/layout/Toast'
import { IconUser, IconMail, IconPhone, IconCheckCircle, IconExternalLink } from '@/components/icons/Icons'
import type { RolContacto } from '@/lib/types'

export interface ContactoConRoles {
  id?: number
  nombre: string
  apellido: string | null
  email: string | null
  telefono: string | null
  cargo: string | null
  roles: RolContacto[]
  es_contabilidad?: boolean
  auth_user_id?: string | null
  portal_activo?: boolean
  onboarding_completado?: boolean
}

interface TabContactosProps {
  contactos: ContactoConRoles[]
  empresaId: number
  empresaNombre: string
  onRefresh: () => void
  /**
   * Dar acceso al portal crea una identidad en `usuarios`: es un alta nuestra,
   * no del partner sobre el contacto de su cliente.
   */
  permiteAltaPortal?: boolean
}

const rolBadgeColors: Record<string, string> = {
  PRINCIPAL: '#00d2d3',
  'FACTURACIÓN': '#f39c12',
  'TÉCNICO': '#3498db',
  COMERCIAL: '#2ecc71',
  GERENCIA: '#9b59b6',
}

export default function TabContactos({ contactos, empresaId, onRefresh, permiteAltaPortal = true }: TabContactosProps) {
  const [creatingPortal, setCreatingPortal] = useState<number | null>(null)

  async function handleCreatePortalAccount(ct: ContactoConRoles) {
    if (!ct.id || !ct.email) return
    const canAccess = ct.roles.includes('PRINCIPAL') || ct.roles.includes('FACTURACIÓN')
    if (!canAccess) {
      showToast('Solo contactos PRINCIPAL o FACTURACION pueden tener acceso al portal', 'error')
      return
    }
    setCreatingPortal(ct.id)
    try {
      const { data, error } = await supabase.functions.invoke('register-client-user', {
        body: {
          contacto_id: ct.id,
          cliente_id: empresaId,
          email: ct.email,
          nombre: `${ct.nombre} ${ct.apellido || ''}`.trim(),
          roles: ct.roles,
        },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)
      onRefresh()
      showToast(`Cuenta creada. Se envio email a ${ct.email}`, 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al crear cuenta', 'error')
    } finally {
      setCreatingPortal(null)
    }
  }

  if (contactos.length === 0) {
    return (
      <EmptyState
        icon={<IconUser size={40} />}
        title="Sin contactos"
        description="Esta empresa aun no tiene contactos vinculados."
      />
    )
  }

  return (
    <div className="empresa-contactos-list">
      {contactos.map((ct, idx) => (
        <div key={ct.id ?? idx} className="empresa-contact-card">
          <div className="empresa-contact-card__header">
            <div className="empresa-contact-card__avatar">
              <IconUser size={20} />
            </div>
            <div className="empresa-contact-card__info">
              <h4 className="empresa-contact-card__name">
                {ct.nombre} {ct.apellido || ''}
              </h4>
              {ct.cargo && (
                <span className="empresa-contact-card__cargo">{ct.cargo}</span>
              )}
            </div>
            <div className="empresa-contact-card__status">
              {ct.portal_activo ? (
                <span className="empresa-portal-badge empresa-portal-badge--active">
                  <IconCheckCircle size={12} />
                  Portal Activo
                </span>
              ) : permiteAltaPortal && (ct.roles.includes('PRINCIPAL') || ct.roles.includes('FACTURACIÓN')) && ct.email ? (
                <button
                  className="btn btn-sm empresa-portal-btn"
                  disabled={creatingPortal === ct.id}
                  onClick={() => handleCreatePortalAccount(ct)}
                  type="button"
                >
                  {creatingPortal === ct.id ? (
                    <div className="auth-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                  ) : (
                    <IconExternalLink size={12} />
                  )}
                  Crear Cuenta Portal
                </button>
              ) : null}
            </div>
          </div>

          <div className="empresa-contact-card__details">
            {ct.email && (
              <span className="empresa-contact-card__detail">
                <IconMail size={13} />
                {ct.email}
              </span>
            )}
            {ct.telefono && (
              <span className="empresa-contact-card__detail">
                <IconPhone size={13} />
                {ct.telefono}
              </span>
            )}
          </div>

          <div className="empresa-contact-card__roles">
            {ct.roles.map(r => (
              <span
                key={r}
                className="empresa-role-badge"
                style={{ background: rolBadgeColors[r] || 'var(--text-muted)', color: 'var(--text-on-primary)' }}
              >
                {r}
              </span>
            ))}
            {ct.es_contabilidad && (
              <span className="empresa-role-badge" style={{ background: 'var(--warning)', color: 'var(--on-warning)' }}>
                CONTABILIDAD
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
